import AsyncStorage from '@react-native-async-storage/async-storage';
import { AnalyzedItem } from './gemini';

const CACHE_PREFIX = 'gemini_cache_';
const CACHE_INDEX_KEY = 'gemini_cache_index';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_ITEMS = 50;

interface CacheEntry {
  result: AnalyzedItem[];
  timestamp: number;
}

interface CacheIndex {
  keys: string[]; // Ordered by access time (oldest first)
}

/**
 * Generate a simple hash from base64 image data.
 * Uses a fast string hash since we're dealing with large base64 strings.
 */
export function hashImageData(base64Data: string | string[]): string {
  const dataToHash = Array.isArray(base64Data) ? base64Data.join('|') : base64Data;
  
  // Use a simple but effective hash for large strings
  // Take samples from the string to create a fingerprint
  const sampleSize = 1000;
  const samples: string[] = [];
  
  // Sample from beginning, middle, and end
  samples.push(dataToHash.slice(0, sampleSize));
  samples.push(dataToHash.slice(Math.floor(dataToHash.length / 2) - sampleSize / 2, Math.floor(dataToHash.length / 2) + sampleSize / 2));
  samples.push(dataToHash.slice(-sampleSize));
  
  // Include length for uniqueness
  const fingerprint = `${dataToHash.length}_${samples.join('')}`;
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Get cached Gemini result for an image hash.
 * Returns null if not found or expired.
 */
export async function getCachedResult(imageHash: string): Promise<AnalyzedItem[] | null> {
  try {
    const cacheKey = CACHE_PREFIX + imageHash;
    const cached = await AsyncStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const entry: CacheEntry = JSON.parse(cached);
    
    // Check if expired
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      // Remove expired entry
      await AsyncStorage.removeItem(cacheKey);
      await removeFromIndex(imageHash);
      console.log('[GeminiCache] Cache expired for hash:', imageHash);
      return null;
    }
    
    // Update access time in index (move to end)
    await updateIndexAccess(imageHash);
    
    console.log('[GeminiCache] Cache hit for hash:', imageHash);
    return entry.result;
  } catch (error) {
    console.error('[GeminiCache] Error reading cache:', error);
    return null;
  }
}

/**
 * Store Gemini result in cache.
 */
export async function setCachedResult(imageHash: string, result: AnalyzedItem[]): Promise<void> {
  try {
    const cacheKey = CACHE_PREFIX + imageHash;
    const entry: CacheEntry = {
      result,
      timestamp: Date.now(),
    };
    
    // Enforce max cache size with LRU eviction
    await enforceMaxCacheSize();
    
    // Store the result
    await AsyncStorage.setItem(cacheKey, JSON.stringify(entry));
    
    // Add to index
    await addToIndex(imageHash);
    
    console.log('[GeminiCache] Cached result for hash:', imageHash);
  } catch (error) {
    console.error('[GeminiCache] Error writing cache:', error);
  }
}

/**
 * Get the cache index (list of cached keys ordered by access time).
 */
async function getCacheIndex(): Promise<CacheIndex> {
  try {
    const indexData = await AsyncStorage.getItem(CACHE_INDEX_KEY);
    if (indexData) {
      return JSON.parse(indexData);
    }
  } catch (error) {
    console.error('[GeminiCache] Error reading index:', error);
  }
  return { keys: [] };
}

/**
 * Save the cache index.
 */
async function saveCacheIndex(index: CacheIndex): Promise<void> {
  await AsyncStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(index));
}

/**
 * Add a key to the index (at the end, most recently accessed).
 */
async function addToIndex(imageHash: string): Promise<void> {
  const index = await getCacheIndex();
  // Remove if exists (will re-add at end)
  index.keys = index.keys.filter(k => k !== imageHash);
  index.keys.push(imageHash);
  await saveCacheIndex(index);
}

/**
 * Remove a key from the index.
 */
async function removeFromIndex(imageHash: string): Promise<void> {
  const index = await getCacheIndex();
  index.keys = index.keys.filter(k => k !== imageHash);
  await saveCacheIndex(index);
}

/**
 * Update access time for a key (move to end of index).
 */
async function updateIndexAccess(imageHash: string): Promise<void> {
  await addToIndex(imageHash); // Same logic - removes and re-adds at end
}

/**
 * Enforce max cache size by evicting oldest entries (LRU).
 */
async function enforceMaxCacheSize(): Promise<void> {
  const index = await getCacheIndex();
  
  while (index.keys.length >= MAX_CACHE_ITEMS) {
    const oldestKey = index.keys.shift();
    if (oldestKey) {
      await AsyncStorage.removeItem(CACHE_PREFIX + oldestKey);
      console.log('[GeminiCache] Evicted oldest cache entry:', oldestKey);
    }
  }
  
  await saveCacheIndex(index);
}

/**
 * Clear all cached Gemini results.
 */
export async function clearGeminiCache(): Promise<void> {
  try {
    const index = await getCacheIndex();
    
    // Remove all cached entries
    for (const key of index.keys) {
      await AsyncStorage.removeItem(CACHE_PREFIX + key);
    }
    
    // Clear the index
    await AsyncStorage.removeItem(CACHE_INDEX_KEY);
    
    console.log('[GeminiCache] Cache cleared');
  } catch (error) {
    console.error('[GeminiCache] Error clearing cache:', error);
  }
}
